
import React from 'react';
import { SbtBadge, Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { ShieldCheck, Activity, Zap, Globe, Star, Medal, Award } from 'lucide-react';

interface SbtBadgeListProps {
  badges: SbtBadge[];
  language: Language;
}

export const SbtBadgeList: React.FC<SbtBadgeListProps> = ({ badges, language }) => {
  const t = TRANSLATIONS[language];

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Activity': return <Activity size={24} />;
      case 'ShieldCheck': return <ShieldCheck size={24} />;
      case 'Zap': return <Zap size={24} />;
      case 'Globe': return <Globe size={24} />;
      case 'Star': return <Star size={24} />;
      case 'Medal': return <Medal size={24} />;
      default: return <Award size={24} />;
    }
  };

  const getRarityColor = (rarity: SbtBadge['rarity']) => {
    switch (rarity) {
      case 'Legendary': return 'text-amber-400 bg-amber-500/10 border-amber-500/50 shadow-amber-500/20';
      case 'Epic': return 'text-purple-400 bg-purple-500/10 border-purple-500/50 shadow-purple-500/20';
      case 'Rare': return 'text-blue-400 bg-blue-500/10 border-blue-500/50 shadow-blue-500/20';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/50';
    }
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
           <h3 className="font-bold text-white text-lg flex items-center gap-2">
              <Medal className="text-indigo-400" />
              {t.sbtBadges}
           </h3>
           <p className="text-sm text-slate-400">{t.sbtSubtitle}</p>
        </div>
      </div>
      
      {badges && badges.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {badges.map(badge => (
            <div 
              key={badge.id}
              className={`group relative p-4 rounded-xl border flex flex-col items-center text-center transition-all hover:scale-105 hover:shadow-lg ${getRarityColor(badge.rarity)}`}
            >
              <div className="mb-2 p-2 rounded-full bg-slate-900/50">
                 {getIcon(badge.icon)}
              </div>
              <h4 className="font-bold text-sm mb-1">{badge.name}</h4>
              <span className="text-[10px] uppercase tracking-wider font-bold opacity-80 mb-2">{badge.rarity}</span>
              
              {/* Tooltip on Hover */}
              <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
                 <p className="text-xs text-slate-200 mb-2">{badge.description}</p>
                 <p className="text-[10px] text-slate-500">{badge.dateEarned}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center p-8 border border-dashed border-slate-700 rounded-xl">
           <p className="text-slate-500 text-sm">No badges earned yet.</p>
        </div>
      )}
    </div>
  );
};
